import { useCallback, useMemo, useRef } from 'react';
import {
  EAutoRange,
  EAxisAlignment,
  ECoordinateMode,
  EHorizontalAnchorPoint,
  EVerticalAnchorPoint,
  FastLineRenderableSeries,
  FastMountainRenderableSeries,
  HorizontalLineAnnotation,
  NativeTextAnnotation,
  NumberRange,
  NumericAxis,
  RolloverModifier,
  type SciChartSurface,
  StackedMountainCollection,
  StackedMountainRenderableSeries,
  VerticalLineAnnotation,
  XyDataSeries,
} from 'scichart';
import {
  SciChartView,
  type SciChartInitContext,
} from 'components/charts/scichart';
import { fmtUsd } from './interest-modeling-format';
import {
  EXPENSE_COLOR,
  EXPENSE_LABEL,
  RETURN_COLOR,
  RETURN_LABEL,
  resolveChartData,
  type CarryChartModel,
  type ChartRenderData,
  type ChartView,
} from './interest-modeling-preview';
import styles from './carry-portfolio.module.scss';

const TICK_COLOR = '#525252';

export interface CarryChartProps {
  /** The assembled chart model (portfolio curves + per-group/well bands). */
  model: CarryChartModel;
  /** The active chart mode. */
  view: ChartView;
  /** The tract the scoped views focus on (aggregate ignores it). */
  focusTractId: string | null;
  /** Shows the SciChartView loading overlay while a reprice is in flight. */
  isLoading: boolean;
}

/**
 * The carried-interest portfolio chart — a SciChart port of the PM mock's
 * CarryChart onto the repo `SciChartView` wrapper (which owns the WASM lifecycle,
 * loading overlay, reset-zoom, and image export). `initChart` sets up the project-month
 * X axis, the cumulative-net Y axis (adaptive-currency labels), and the rollover
 * tooltip ONCE; `renderData` rebuilds the series + annotations on every data/view/focus
 * change: a cumulative return mountain (aggregate) or one stacked band per group/well
 * (scoped), the cumulative expense line on top, per-group payout markers, and a
 * breakeven marker matching the view scope. A legend below the canvas mirrors the drawn
 * series (colors match the summary table).
 *
 * @param {CarryChartProps} props - The chart model, view, focused tract, and loading flag.
 * @return {JSX.Element} The chart card.
 */
export function CarryChart({
  model,
  view,
  focusTractId,
  isLoading,
}: CarryChartProps) {
  const data = useMemo(
    () => resolveChartData(model, view, focusTractId),
    [model, view, focusTractId]
  );
  // The stable init/render callbacks read the latest draw data via a ref.
  const dataRef = useRef<ChartRenderData>(data);
  dataRef.current = data;

  const initChart = useCallback(
    ({ surface, wasmContext }: SciChartInitContext) => {
      const xAxis = new NumericAxis(wasmContext, {
        axisTitle: 'Project month',
        axisTitleStyle: { fontSize: 12, color: TICK_COLOR },
        labelStyle: { fontSize: 10, color: TICK_COLOR },
        autoTicks: false,
        majorDelta: 12,
        minorDelta: 12,
        drawMinorGridLines: false,
        drawMinorTickLines: false,
        drawMajorBands: false,
        growBy: new NumberRange(0.02, 0.02),
        autoRange: EAutoRange.Always,
        axisAlignment: EAxisAlignment.Bottom,
      });
      const yAxis = new NumericAxis(wasmContext, {
        axisTitle: 'Cumulative net',
        axisTitleStyle: { fontSize: 12, color: TICK_COLOR },
        labelStyle: { fontSize: 10, color: TICK_COLOR },
        drawMajorBands: false,
        minorsPerMajor: 1,
        growBy: new NumberRange(0, 0.08),
        autoRange: EAutoRange.Always,
        axisAlignment: EAxisAlignment.Left,
      });
      yAxis.labelProvider.formatLabel = (value: number) => fmtUsd(value);
      surface.xAxes.add(xAxis);
      surface.yAxes.add(yAxis);

      surface.chartModifiers.add(
        new RolloverModifier({
          showRolloverLine: true,
          rolloverLineStroke: '#8d8d8d',
          snapToDataPoint: true,
          tooltipDataTemplate: (seriesInfo) => [
            `${seriesInfo.seriesName}: ${fmtUsd(seriesInfo.yValue)}`,
          ],
        })
      );
    },
    []
  );

  const renderData = useCallback(
    ({ surface, wasmContext }: SciChartInitContext) => {
      const draw = dataRef.current;
      surface.renderableSeries.clear();
      surface.annotations.clear();
      const { months } = draw;

      if (draw.aggregateReturn) {
        const returnSeries = new FastMountainRenderableSeries(wasmContext, {
          dataSeries: new XyDataSeries(wasmContext, {
            xValues: months,
            yValues: draw.aggregateReturn,
            dataSeriesName: RETURN_LABEL,
          }),
          fill: `${RETURN_COLOR}14`,
          stroke: RETURN_COLOR,
          strokeThickness: 2.5,
          zeroLineY: 0,
        });
        styleTooltip(returnSeries, RETURN_COLOR);
        surface.renderableSeries.add(returnSeries);
      } else if (draw.stackRows) {
        const collection = new StackedMountainCollection(wasmContext);
        draw.stackRows.forEach((row) => {
          const series = new StackedMountainRenderableSeries(wasmContext, {
            dataSeries: new XyDataSeries(wasmContext, {
              xValues: months,
              yValues: row.values,
              dataSeriesName: row.label,
            }),
            fill: `${row.color}80`,
            stroke: row.color,
            strokeThickness: 1,
          });
          styleTooltip(series, row.color);
          collection.add(series);
        });
        surface.renderableSeries.add(collection);
      }

      const expenseSeries = new FastLineRenderableSeries(wasmContext, {
        dataSeries: new XyDataSeries(wasmContext, {
          xValues: months,
          yValues: draw.expenseValues,
          dataSeriesName: EXPENSE_LABEL,
        }),
        stroke: EXPENSE_COLOR,
        strokeThickness: 2.5,
      });
      styleTooltip(expenseSeries, EXPENSE_COLOR);
      surface.renderableSeries.add(expenseSeries);

      surface.annotations.add(
        new HorizontalLineAnnotation({
          y1: 0,
          stroke: '#a8a8a8',
          strokeThickness: 1,
        })
      );

      draw.payoutMarkers.forEach((marker) =>
        addVerticalMarker(
          surface,
          marker.month,
          marker.label,
          marker.color,
          [6, 4],
          1
        )
      );
      if (draw.breakeven) {
        addVerticalMarker(
          surface,
          draw.breakeven.month,
          draw.breakeven.label,
          draw.breakeven.color,
          [2, 3],
          1.5
        );
      }
    },
    []
  );

  return (
    <div className={styles.chartCard}>
      <SciChartView
        initChart={initChart}
        renderData={renderData}
        renderDeps={[data]}
        isLoading={isLoading}
        height={440}
        showResetZoom
        exportConfig={{
          label: 'Carry portfolio',
          filenamePrefix: 'carry-portfolio',
        }}
      />
      <div className={styles.legend}>
        {data.legend.map((item) => (
          <span key={item.label} className={styles.legendItem}>
            <span
              className={styles.legendSwatch}
              style={{ background: item.color }}
            />
            {item.label}
          </span>
        ))}
      </div>
    </div>
  );
}

// White tooltip text on the series' own color for a legible rollover (mock parity).
function styleTooltip(
  series: {
    rolloverModifierProps: { tooltipColor: string; tooltipTextColor: string };
  },
  color: string
) {
  series.rolloverModifierProps.tooltipColor = color;
  series.rolloverModifierProps.tooltipTextColor = '#ffffff';
}

// A dashed vertical line with a 90°-rotated label running up the line near the top of
// the plot (mock parity — the old Highcharts plot-line labels).
function addVerticalMarker(
  surface: SciChartSurface,
  x: number,
  label: string,
  color: string,
  dash: number[],
  thickness: number
) {
  surface.annotations.add(
    new VerticalLineAnnotation({
      x1: x,
      stroke: color,
      strokeThickness: thickness,
      strokeDashArray: dash,
      showLabel: false,
    })
  );
  surface.annotations.add(
    new NativeTextAnnotation({
      x1: x,
      y1: 0.02,
      xCoordinateMode: ECoordinateMode.DataValue,
      yCoordinateMode: ECoordinateMode.Relative,
      text: label,
      textColor: color,
      fontSize: 10,
      rotation: 90,
      horizontalAnchorPoint: EHorizontalAnchorPoint.Left,
      verticalAnchorPoint: EVerticalAnchorPoint.Bottom,
    })
  );
}
